import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import styles from './EvFilterSection.module.css';

const cx = classnames.bind(styles);

/**
 * EvFilterSection - A collapsible filter section with multi-select options
 */
export default function EvFilterSection({
  title,
  options,
  selectedValues,
  onChange,
  isExpanded,
  onToggle,
}) {
  const handleOptionToggle = useCallback((value) => {
    const isSelected = selectedValues.includes(value);
    let newValues;

    if (isSelected) {
      newValues = selectedValues.filter(v => v !== value);
    } else {
      newValues = [...selectedValues, value];
    }

    onChange(newValues);
  }, [selectedValues, onChange]);

  const handleSelectAll = useCallback(() => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(o => o.value));
    }
  }, [selectedValues, options, onChange]);

  const allSelected = selectedValues.length === options.length;
  const someSelected = selectedValues.length > 0 && selectedValues.length < options.length;

  // Get summary text for collapsed state
  const getSummaryText = () => {
    if (selectedValues.length === 0) {
      return 'All';
    }
    if (selectedValues.length === options.length) {
      return 'All';
    }
    if (selectedValues.length === 1) {
      const selected = options.find(o => o.value === selectedValues[0]);
      return selected?.label || '1 selected';
    }
    return `${selectedValues.length} selected`;
  };

  return (
    <div className={cx('filterSection')}>
      <button
        type="button"
        className={cx('sectionHeader')}
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className={cx('sectionTitle')}>{title}</span>
        <span className={cx('summaryText')}>{getSummaryText()}</span>
        <span className={cx('expandIcon', { expanded: isExpanded })}>
          {isExpanded ? '−' : '+'}
        </span>
      </button>

      {isExpanded && (
        <div className={cx('sectionContent')}>
          {options.length > 2 && (
            <label className={cx('optionLabel', 'selectAll')}>
              <input
                type="checkbox"
                className={cx('optionCheckbox')}
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = someSelected;
                  }
                }}
                onChange={handleSelectAll}
              />
              <span className={cx('optionText')}>Select All</span>
            </label>
          )}

          {options.map(({ value, label, count }) => (
            <label key={value} className={cx('optionLabel')}>
              <input
                type="checkbox"
                className={cx('optionCheckbox')}
                checked={selectedValues.includes(value)}
                onChange={() => handleOptionToggle(value)}
              />
              <span className={cx('optionText')}>
                {label}
                {count !== undefined && (
                  <span className={cx('optionCount')}>({count})</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

EvFilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number,
  })).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
};

EvFilterSection.defaultProps = {
  selectedValues: [],
  isExpanded: true,
  onToggle: undefined,
};
